import { fgColumnEntries } from "@redotech/redo-fulfillment-common/views/fulfillment/fg-column-catalog";
import {
  FulfillmentGroupData,
  FulfillmentGroupPickStatus,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  getMissingPickedItems,
  getPickedMapByExternalId,
} from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import AlertCircleIcon from "@redotech/redo-icons/alert-circle.svg";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import XCircleIcon from "@redotech/redo-icons/x-circle.svg";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { ColumnAlignment } from "@redotech/redo-web/table";
import { Column2 } from "@redotech/redo-web/table2";
import { getExpandedItems } from "../../utils/outbound-labels-utils";
import { ItemInfo, ItemNameCell } from "../cells/item-name-cell";

const catalogEntry = fgColumnEntries.missingPickItems;

export function MissingPickItemsColumn({
  hideInputItems,
}: {
  hideInputItems: boolean;
}): Column2<FulfillmentGroupData> {
  return {
    alignment: ColumnAlignment.LEFT,
    key: catalogEntry.key,
    title: catalogEntry.title,
    sort: catalogEntry.sortDirection,
    renderNormalCell: ({ row }) => {
      // Only show missing items for PROBLEM orders
      if (row.pickStatus !== FulfillmentGroupPickStatus.PROBLEM) {
        return <ItemNameCell isWaitingForSplit={false} lineItems={[]} />;
      }

      const missingItems = getMissingPickedItems(row, { hideInputItems });

      return (
        <ItemNameCell
          isWaitingForSplit={false}
          lineItems={missingItems}
          showBin
        />
      );
    },
    renderExpandedCell: ({ row, expansionIndex }) => {
      if (row.pickStatus !== FulfillmentGroupPickStatus.PROBLEM) {
        return null;
      }

      const expandedItems = getExpandedItems(row);
      const lineItem = expandedItems?.[expansionIndex];
      if (!lineItem) {
        return null;
      }

      const pickedByExternalId = getPickedMapByExternalId(row);
      const pickedQty = pickedByExternalId.get(lineItem.externalId) ?? 0;

      // Count this item's position (1-based) among items with the same externalId
      let positionInGroup = 0;
      for (let i = 0; i <= expansionIndex; i++) {
        if (expandedItems[i]?.externalId === lineItem.externalId) {
          positionInGroup++;
        }
      }

      // First `pickedQty` items are picked, rest are missing
      const isMissing = positionInGroup > pickedQty;

      // Check if a missing item is resolvable (warehouse has enough inventory)
      let isResolvable = false;
      if (isMissing && row.lineItemPickability) {
        const pickabilityEntry = row.lineItemPickability.find(
          (li) => li.externalId === lineItem.externalId,
        );
        isResolvable = pickabilityEntry?.resolvable === true;
      }

      const icon = !isMissing
        ? CheckCircleIcon
        : isResolvable
          ? AlertCircleIcon
          : XCircleIcon;
      const color = !isMissing ? "success" : isResolvable ? "warning" : "error";

      return (
        <Flex align="center" gap="md" pl="lg">
          <RedoIcon color={color} Icon={icon} size={16} />
          <ItemInfo lineItem={lineItem} />
        </Flex>
      );
    },
    width: catalogEntry.defaultWidth,
  };
}
